<template>
  <div class="consumption-detail-container">
    <h2>消费记录详情</h2>
    <el-card shadow="hover">
      <el-descriptions title="基本信息" :column="2">
        <el-descriptions-item label="ID">{{ consumptionDetail.id }}</el-descriptions-item>
        <el-descriptions-item label="用户ID">{{ consumptionDetail.user_id }}</el-descriptions-item>
        <el-descriptions-item label="用户名称">{{ consumptionDetail.user?.name || '未知' }}</el-descriptions-item>
        <el-descriptions-item label="消费单号">{{ consumptionDetail.consumption_no }}</el-descriptions-item>
        <el-descriptions-item label="消费类型">{{ getTypeName(consumptionDetail.type) }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ getStatusName(consumptionDetail.status) }}</el-descriptions-item>
        <el-descriptions-item label="消费时间">{{ formatDate(consumptionDetail.consumption_time) }}</el-descriptions-item>
        <el-descriptions-item label="总金额">¥{{ consumptionDetail.total_amount }}</el-descriptions-item>
        <el-descriptions-item label="描述" :span="2">{{ consumptionDetail.description || '-' }}</el-descriptions-item>
      </el-descriptions>
      
      <div style="margin-top: 20px;">
        <h3>消费项目列表</h3>
        <el-table :data="consumptionDetail.items" border style="width: 100%">
          <el-table-column prop="name" label="项目名称" />
          <el-table-column prop="quantity" label="数量" />
          <el-table-column prop="unit_price" label="单价" />
          <el-table-column prop="total_price" label="总价" />
          <el-table-column prop="created_at" label="添加时间" width="180" />
          <el-table-column label="附件" width="200">
            <template #default="scope">
              <div class="attachments">
                <div v-if="scope.row.attachment" class="attachment-list">
                  <el-link
                    v-for="(url, index) in JSON.parse(scope.row.attachment)"
                    :key="index"
                    :href="url"
                    target="_blank"
                    type="primary"
                    style="margin-right: 10px; margin-bottom: 5px; display: inline-block;"
                  >
                    <el-icon><Document /></el-icon> 附件{{ index + 1 }}
                  </el-link>
                </div>
                <el-button
                  type="primary"
                  size="small"
                  @click="handleUploadAttachments(scope.row)"
                >
                  <el-icon><Upload /></el-icon> 上传附件
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 附件上传弹窗 -->
      <el-dialog
        v-model="uploadDialogVisible"
        title="上传附件"
        width="50%"
      >
        <el-upload
          ref="uploadRef"
          :action="uploadUrl"
          :headers="{
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }"
          :data="{}"
          :file-list="fileList"
          :auto-upload="false"
          :multiple="true"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
        >
          <el-button type="primary">
            <el-icon><Upload /></el-icon> 选择文件
          </el-button>
        </el-upload>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="uploadDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="submitUpload">确定上传</el-button>
          </span>
        </template>
      </el-dialog>
      
      <div class="detail-actions">
        <el-button @click="handleBack">返回</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import { Document, Upload } from '@element-plus/icons-vue';
import request from '../../api/axios';

export default {
  name: 'ConsumptionDetail',
  components: {
    Document,
    Upload
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const consumptionId = route.params.id;
    
    const consumptionDetail = ref({
      id: '',
      user_id: '',
      user: null,
      consumption_no: '',
      type: '',
      status: '',
      consumption_time: '',
      total_amount: 0,
      description: '',
      items: []
    });
    
    // 附件上传相关
    const uploadDialogVisible = ref(false);
    const currentItem = ref(null);
    const uploadUrl = ref('');
    const uploadRef = ref(null);
    const fileList = ref([]);
    
    // 获取消费记录详情
    const fetchConsumptionDetail = async () => {
      try {
        const response = await request({
          url: `/manage/consumptions/${consumptionId}`,
          method: 'get'
        });
        consumptionDetail.value = response.data;
      } catch (error) {
        ElMessage.error('获取消费记录详情失败');
        console.error(error);
        router.push('/consumptions/list');
      }
    };
    
    // 页面加载时获取消费记录详情
    onMounted(() => {
      fetchConsumptionDetail();
    });
    
    // 获取消费类型名称
    const getTypeName = (type) => {
      const typeMap = {
        'room': '房间消费',
        'food': '餐饮消费',
        'entertainment': '娱乐消费',
        'other': '其他消费'
      };
      return typeMap[type] || type;
    };
    
    // 获取状态名称
    const getStatusName = (status) => {
      const statusMap = {
        'completed': '已完成',
        'pending': '未完成'
      };
      return statusMap[status] || status;
    };
    
    // 格式化日期
    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };
    
    // 返回列表页面
    const handleBack = () => {
      router.push('/consumptions/list');
    };
    
    // 处理附件上传
    const handleUploadAttachments = (item) => {
      currentItem.value = item;
      uploadUrl.value = `/manage/consumption-items/${item.id}/attachments`;
      uploadDialogVisible.value = true;
      fileList.value = [];
    };
    
    // 提交上传
    const submitUpload = () => {
      uploadRef.value.submit();
    };
    
    // 上传成功处理
    const handleUploadSuccess = (response) => {
      if (response.code === 200) {
        ElMessage.success('附件上传成功');
        uploadDialogVisible.value = false;
        // 刷新消费记录详情
        fetchConsumptionDetail();
      } else {
        ElMessage.error('附件上传失败');
      }
    };
    
    // 上传失败处理
    const handleUploadError = () => {
      ElMessage.error('附件上传失败');
    };
    
    return {
      consumptionDetail,
      getTypeName,
      getStatusName,
      formatDate,
      handleBack,
      uploadDialogVisible,
      currentItem,
      uploadUrl,
      uploadRef,
      fileList,
      handleUploadAttachments,
      submitUpload,
      handleUploadSuccess,
      handleUploadError
    };
  }
};
</script>

<style scoped>
.consumption-detail-container {
  padding: 20px;
}

.consumption-detail-container h2 {
  margin-bottom: 20px;
  color: #333;
}

.consumption-detail-container h3 {
  margin-bottom: 10px;
  color: #333;
  font-size: 16px;
}

.detail-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-start;
}

/* 附件样式 */
.attachments {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.attachment-list {
  margin-bottom: 5px;
  display: flex;
  flex-wrap: wrap;
}
</style>